'use client';

/**
 * BadgeCell Component
 *
 * Renders badges using the existing Badge component from the UI library.
 * Provides integration with Radix UI Badge patterns.
 *
 * Typography uses inline styles to avoid Tailwind arbitrary value conflicts.
 * See tokens.ts for detailed explanation of the Tailwind CSS variable issue.
 */

import React from 'react';
import Image from 'next/image';
import { cn } from '@/lib/utils';
import { LucideIcon } from 'lucide-react';
import { defaultTheme, getTextStyles } from '../theme/tokens';

export type BadgeVariant = 'default' | 'secondary' | 'success' | 'warning' | 'destructive' | 'outline';

interface BadgeCellProps {
  value: any;
  variant?: BadgeVariant;
  icon?: LucideIcon;
  imageUrl?: string;
  colorMap?: Record<string, BadgeVariant>;
  className?: string;
}

const variantClasses: Record<BadgeVariant, string> = {
  default: 'bg-primary-600 text-white',
  secondary: 'bg-slate-100 text-slate-700',
  success: 'bg-emerald-100 text-emerald-700',
  warning: 'bg-amber-100 text-amber-700',
  destructive: 'bg-red-100 text-red-700',
  outline: 'border border-slate-300 bg-white text-slate-700',
};

export const BadgeCell: React.FC<BadgeCellProps> = ({
  value,
  variant = 'secondary',
  icon: Icon,
  imageUrl,
  colorMap,
  className,
}) => {
  const displayValue = value != null ? String(value) : '-';

  // Map value to variant if colorMap provided
  const resolvedVariant = colorMap?.[displayValue] ?? variant;

  // Get typography styles from theme (inline styles to avoid Tailwind conflicts)
  const textStyles = getTextStyles(defaultTheme);

  return (
    <div className="inline-flex items-center justify-center">
      <div
        className={cn(
          'inline-flex items-center gap-2 w-fit',
          'rounded-md px-2.5 py-1',
          variantClasses[resolvedVariant],
          className
        )}
        style={textStyles.ui()}
      >
        {imageUrl && (
          <Image
            src={imageUrl}
            alt=""
            width={16}
            height={16}
            className="flex-shrink-0"
          />
        )}
        {Icon && <Icon className="h-3.5 w-3.5" />}
        <span>{displayValue}</span>
      </div>
    </div>
  );
};
